import { useState } from 'react';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DownloadIcon from '@mui/icons-material/Download';
import GavelIcon from '@mui/icons-material/Gavel';
import GridViewIcon from '@mui/icons-material/GridView';
import HandymanIcon from '@mui/icons-material/Handyman';
import HomeIcon from '@mui/icons-material/Home';
import LanguageIcon from '@mui/icons-material/Language';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PaidIcon from '@mui/icons-material/Paid';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import RadarIcon from '@mui/icons-material/Radar';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WindowIcon from '@mui/icons-material/Window';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import * as Styled from './ReportPanel.style';
import ReportSection from './ReportSection';
import { BUSINESS_IDEA_SECTIONS, CLICKSTREAM_COMMON_PROPS } from '../utils/constants';

/**
 * Main content panel displaying detailed business report
 * Dynamically renders report sections based on backend data structure.
 * Can be used for both Business Ideas and Business Names.
 *
 * Supports two backend data formats:
 * 1. Future format: { report: { sections: [{ id, title, content }] } }
 * 2. Current format: { report: { overview: "...", targetCustomers: "...", ... } }
 *
 * Icons are mapped based on section IDs, with a fallback for unmapped sections.
 *
 * @param {object} props - Component props
 * @param {object} props.selectedIdea - Currently selected item (business idea or name)
 * @param {Function} [props.onDownloadReport] - Handler for download report action (optional)
 * @param {boolean} [props.loading=false] - Whether content is loading
 * @param {string} [props.emptyMessage='Select an item to view detailed report'] - Message when no item selected
 * @component
 */
function ReportPanel({
  selectedIdea,
  onDownloadReport,
  loading = false,
  emptyMessage = 'Select an item to view detailed report'
}) {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const showDownload = Boolean(onDownloadReport);

  const handleDownloadReport = async () => {
    if (downloadLoading || loading || !onDownloadReport) return;

    setDownloadLoading(true);
    if (selectedIdea) {
      await onDownloadReport(selectedIdea.id);
    }
    setDownloadLoading(false);
  };

  if (!selectedIdea) {
    return (
      <Styled.MainContent>
        <Styled.ContentHeader>
          <Styled.ContentTitle color="secondary" size="sm">
            {emptyMessage}
          </Styled.ContentTitle>
        </Styled.ContentHeader>
      </Styled.MainContent>
    );
  }

  // Icon mapping based on section IDs (supports both old camelCase and new snake_case)
  const iconMap = {
    // New format (snake_case)
    overview: <GridViewIcon />,
    targetCustomers: <PeopleIcon />,
    revenueModel: <CurrencyExchangeIcon />,
    startupCosts: <AttachMoneyIcon />,
    marketingStrategy: <MyLocationIcon />,
    risks: <WarningAmberIcon />,
    growthOpportunities: <TrendingUpIcon />,
    whyThisWorksForYou: null,
    businessOverview: <GridViewIcon />,
    startupCostsInvestment: <AttachMoneyIcon />,
    risksChallenges: <WarningAmberIcon />,
    // START BUSINESS COST
    annualReport: <AssignmentIcon />,
    bookkeepingTools: <AccountCircleIcon />,
    formation: <WindowIcon />,
    generalOperatingCosts: <PaidIcon />,
    licensesPermits: <AssignmentIndIcon />,
    officeOrPhysicalLocation: <HomeIcon />,
    operatingEquipment: <HandymanIcon />,
    registeredAgent: <SecurityIcon />,
    summary: null,
    taxes: <CurrencyExchangeIcon />,
    estimatedTotal: <ViewStreamIcon />,
    websiteMarketingExpenses: <PublicIcon />,
    // END BUSINESS COST
    // Old format (camelCase)
    why_this_works_for_you: null,
    business_overview: <GridViewIcon />,
    target_customers: <PeopleIcon />,
    revenue_model: <CurrencyExchangeIcon />,
    startup_costs_and_investment: <AttachMoneyIcon />,
    'startup_costs_&_investment': <AttachMoneyIcon />,
    marketing_strategy: <RadarIcon />,
    risks_and_challenges: <WarningAmberIcon />,
    'risks_&_challenges': <WarningAmberIcon />,
    growth_opportunities: <TrendingUpIcon />,
    similar_businesses: <BusinessIcon />,
    domains: <LanguageIcon />,
    trademark: <GavelIcon />,

    // Add fallback for unmapped sections
    default: <GridViewIcon />
  };

  // Get icon for section ID
  const getIconForSection = (sectionId) => {
    if (iconMap[sectionId] === null) return null;
    return iconMap[sectionId] || iconMap.default;
  };

  // Helper function to format section titles from camelCase or snake_case keys
  const formatSectionTitle = (key) => {
    return key
      .replace(/_/g, ' ') // Replace underscores with spaces (for snake_case)
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters (for camelCase)
      .replace(/&/g, 'and') // Replace & with 'and'
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Build report sections dynamically from backend data
  // Support both new (business_plan) and old (report) format
  const businessPlan = selectedIdea.businessPlan || selectedIdea.report || selectedIdea || {};

  const reportSections = businessPlan?.sections
    ? // If business_plan has sections array (future backend structure)
      businessPlan.sections.map((section) => ({
        id: section.id,
        title: section.title,
        icon: getIconForSection(section.id),
        content: section.content,
        component: section.component
      }))
    : // Fallback: convert current flat business_plan structure to sections
      Object.entries(businessPlan || {})
        .filter(([key, value]) => key !== 'sections' && value) // Filter out empty values and sections key
        .map(([key, value]) => ({
          id: key,
          title: formatSectionTitle(key),
          icon: getIconForSection(key),
          content: value
        }));

  return (
    <Styled.MainContent>
      <Styled.ContentHeader>
        <Text color="secondary" size="sm">
          Detailed report
        </Text>

        <Styled.ContentSubHeader>
          <Box>
            <Text color="primary" mb={1} size="xl" weight="medium">
              {selectedIdea.title}
            </Text>
            {selectedIdea.description && (
              <Text color="secondary" size="md">
                {selectedIdea.description}
              </Text>
            )}
          </Box>
          {showDownload && (
            <Styled.DownloadButton
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: 'business_idea_download',
                elementText: selectedIdea.title
              }}
              color="primary"
              disabled={downloadLoading || loading}
              loading={downloadLoading}
              size="small"
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={handleDownloadReport}
            >
              Download report
            </Styled.DownloadButton>
          )}
        </Styled.ContentSubHeader>
      </Styled.ContentHeader>

      <Styled.ContentBody>
        {BUSINESS_IDEA_SECTIONS.map((section) => {
          const sectionContent = reportSections.find((s) => s.id === section.id);
          if (!sectionContent) return null;
          const componentType = sectionContent.component || section.component;
          return (
            <ReportSection
              key={sectionContent.id}
              content={sectionContent.content}
              icon={sectionContent.icon}
              title={section.title}
              {...(componentType && { component: componentType })}
            />
          );
        })}

        {showDownload && (
          <Styled.FooterSectionContent>
            <Text size="md" weight="medium">
              Ready to take the next step?
            </Text>
            <Text color="secondary" mb={2} size="sm">
              Download this comprehensive business report as a PDF for easy reference and future
              planning.
            </Text>
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: 'business_idea_download',
                elementText: selectedIdea.title
              }}
              color="primary"
              disabled={downloadLoading || loading}
              loading={downloadLoading}
              size="medium"
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={handleDownloadReport}
            >
              Download report
            </Button>
          </Styled.FooterSectionContent>
        )}
      </Styled.ContentBody>
    </Styled.MainContent>
  );
}

ReportPanel.propTypes = {
  selectedIdea: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    businessPlan: PropTypes.object,
    report: PropTypes.object
  }),
  onDownloadReport: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string
};

export default ReportPanel;
